import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { brandApi } from "../lib/brand-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

/**
 * The unified Account workspace.
 *
 * One destination that used to be three tabs: the chain roster (My Chains),
 * the ad-spend view (Mappings), and the ability to correct a mapping (My
 * Review). A store's coverage numbers and the controls to fix that store sit
 * on the SAME row, because "is this number right?" and "this mapping is
 * wrong" are the same thought.
 *
 * Coverage cells are three-state and the distinction matters on screen as
 * much as in the API: an em dash means no fact row exists for that (store,
 * vendor, period); "$0" means the vendor reported a confirmed zero. Rendering
 * both as "$0" would quietly claim we know something we don't.
 */

interface Cell {
  status: "no_data" | "zero" | "spend";
  amount_usd: number | null;
  orders: number | null;
  roas: number | null;
}
interface VendorAlias {
  vendor: string;
  display_name: string | null;
  vendor_account_id: string;
  match_confidence: string | null;
}
interface CoverageStore {
  tenant_store_id: string;
  license_number: string | null;
  name: string | null;
  city: string | null;
  state: string | null;
  status: string;
  included: boolean;
  source: string | null;
  confirmed_by_wtb: boolean;
  confirmed_by_adspend: boolean;
  aliases: VendorAlias[];
  platforms: { dutchie: Cell; iheartjane: Cell };
}
interface CoverageAccount {
  tenant_chain_id: string;
  name: string;
  source_chain_id: string | null;
  counts: {
    canonical_locations: number;
    included_members: number;
    cm_direct: number;
    wtb_confirmed: number;
    adspend_confirmed: number;
    account_only_licenses: number;
  };
  aliases: { dba: Array<{ value: string; source: string }>; vendor: VendorAlias[] };
  account_level: { surfside: Cell };
  stores: CoverageStore[];
}
interface UnresolvedRow {
  vendor: string;
  vendor_account_id: string;
  store_name: string | null;
  state: string | null;
  amount_usd: number | null;
}
interface SearchHit {
  source: string;
  ref: string;
  title: string | null;
  subtitle: string | null;
  license_number: string | null;
  vendor: string | null;
  score: number | null;
}

const SOURCE_BADGE: Record<string, { label: string; cls: string }> = {
  "cm-direct": { label: "CM-direct", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  "cm-expanded": { label: "CM-expanded", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  "user-added": { label: "added manually", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  "wtb-confirmed": { label: "WTB", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "adspend-confirmed": { label: "ad-spend", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const SOURCE_LABEL: Record<string, string> = {
  license: "Licence directory",
  gbp: "Google Business Profile",
  where_to_buy: "Where-to-Buy",
  customer_master: "Customer Master",
  address_book: "Address Book",
  vendor_account: "Vendor accounts (Jane / Dutchie / Surfside)",
};

/** Sources that are public directory data, so a result is safe to widen to. */
const PUBLIC_SOURCES = new Set(["license", "gbp"]);

function money(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function CoverageCell({ cell }: { cell: Cell }) {
  if (cell.status === "no_data") {
    return <span className="text-gray-400" title="No data reported for this store and platform in this period">—</span>;
  }
  if (cell.status === "zero") {
    return (
      <span
        className="inline-block rounded-md bg-gray-100 px-2 py-0.5 text-xs font-mono text-gray-600"
        title="The platform reported a confirmed $0 for this period"
      >
        $0
      </span>
    );
  }
  return (
    <span className="inline-block">
      <span className="inline-block rounded-md bg-amber-50 px-2 py-0.5 text-xs font-mono font-medium text-amber-800">
        {money(cell.amount_usd)}
      </span>
      {(cell.orders != null || cell.roas != null) && (
        <span className="mt-0.5 block text-[10px] text-gray-400">
          {[cell.orders != null ? `${cell.orders} orders` : null, cell.roas != null ? `${cell.roas} ROAS` : null]
            .filter(Boolean)
            .join(" · ")}
        </span>
      )}
    </span>
  );
}

export default function BrandAccountDetail({ slug, chainId }: { slug: string; chainId: string }) {
  const toast = useToast();
  const chainBase = `/api/brand/${encodeURIComponent(slug)}/chains/${encodeURIComponent(chainId)}`;

  const [account, setAccount] = useState<CoverageAccount | null>(null);
  const [unresolved, setUnresolved] = useState<UnresolvedRow[]>([]);
  const [period, setPeriod] = useState<{ period_start: string; period_end: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await brandApi.get(
        `/api/brand/${encodeURIComponent(slug)}/accounts/coverage?tenant_chain_id=${encodeURIComponent(chainId)}`
      );
      setAccount((r.accounts ?? [])[0] ?? null);
      setUnresolved(r.unresolved ?? []);
      setPeriod(r.period ?? null);
    } catch (err) {
      toast.push(`Failed to load account: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [slug, chainId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleMember = async (store: CoverageStore) => {
    const nextStatus = store.included ? "user-excluded" : "auto-included";
    setBusyId(store.tenant_store_id);
    setAccount((prev) =>
      prev
        ? {
            ...prev,
            stores: prev.stores.map((s) =>
              s.tenant_store_id === store.tenant_store_id ? { ...s, included: !s.included, status: nextStatus } : s
            ),
          }
        : prev
    );
    try {
      await brandApi.post(`${chainBase}/members/${encodeURIComponent(store.tenant_store_id)}/toggle`, { status: nextStatus });
      toast.push(nextStatus === "user-excluded" ? "Location excluded." : "Location included.", "success");
      load();
    } catch (err) {
      toast.push(`Could not change that: ${(err as Error).message}`, "error");
      load();
    } finally {
      setBusyId(null);
    }
  };

  const addLocation = async (licenseNumber: string) => {
    try {
      await brandApi.post(`${chainBase}/members/add`, { license_number: licenseNumber });
      toast.push("Location added to this account.", "success");
      load();
    } catch (err) {
      toast.push(`Could not add that store: ${(err as Error).message}`, "error");
    }
  };

  if (loading) {
    return <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">Loading account…</div>;
  }
  if (!account) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
        <p className="text-sm font-medium text-gray-700">Account not found.</p>
        <a href={`#/brand/${encodeURIComponent(slug)}/accounts`} className="mt-2 inline-block text-sm text-accent-700 hover:underline">
          ← Back to Accounts
        </a>
      </div>
    );
  }

  const included = account.stores.filter((s) => s.included).length;

  return (
    <div className="space-y-5">
      <div>
        <a href={`#/brand/${encodeURIComponent(slug)}/accounts`} className="text-xs text-gray-500 hover:text-gray-800">
          ← Accounts
        </a>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{account.name}</h1>
            <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 font-medium text-gray-700">
                {included} included / {account.counts.canonical_locations} canonical
              </span>
              {account.counts.cm_direct > 0 && (
                <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 font-medium text-violet-700">
                  {account.counts.cm_direct} CM-direct
                </span>
              )}
              {account.counts.wtb_confirmed > 0 && (
                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                  {account.counts.wtb_confirmed} WTB-confirmed
                </span>
              )}
              {account.counts.adspend_confirmed > 0 && (
                <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-medium text-amber-700">
                  {account.counts.adspend_confirmed} ad-spend-confirmed
                </span>
              )}
            </div>
          </div>
          {period && (
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">
              Spend period: <span className="font-medium text-gray-900">{period.period_start} → {period.period_end}</span>
            </div>
          )}
        </div>
      </div>

      {(account.aliases.dba.length > 0 || account.aliases.vendor.length > 0) && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Known as, across systems
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            Worth a glance before trusting the numbers below — this is how each platform refers to this account.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {account.aliases.dba.map((a) => (
              <span key={`dba-${a.value}`} className="inline-flex items-baseline gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-600">
                <b className="font-sans text-[9px] uppercase tracking-wide text-gray-400">DBA</b>
                {a.value}
              </span>
            ))}
            {account.aliases.vendor.map((a) => (
              <span
                key={`v-${a.vendor}-${a.vendor_account_id}`}
                className="inline-flex items-baseline gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-600"
                title={a.match_confidence ? `match confidence: ${a.match_confidence}` : undefined}
              >
                <span
                  className={`self-center h-1.5 w-1.5 rounded-full ${
                    a.match_confidence === "high" || a.match_confidence === "manual-confirm" ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                />
                <b className="font-sans text-[9px] uppercase tracking-wide text-gray-400">{a.vendor}</b>
                {a.display_name || a.vendor_account_id}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3">
        <span className="text-xs font-medium text-emerald-900">
          Surfside — account-level (resolves to the account, never to one store)
        </span>
        <span className="rounded-md bg-white px-2 py-0.5 font-mono text-sm font-medium text-amber-800">
          {account.account_level.surfside.status === "no_data" ? "—" : money(account.account_level.surfside.amount_usd)}
        </span>
        <span className="text-xs text-emerald-800/70">
          {account.account_level.surfside.status === "no_data" ? "no data this period" : "media cost"}
        </span>
      </div>

      {unresolved.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            Needs your review — {unresolved.length} unmatched {unresolved.length === 1 ? "row" : "rows"}
          </h3>
          <p className="mt-0.5 text-xs text-amber-900/80">
            Ad spend we couldn't tie to any store. Search below to find the right one and add it, or leave it — nothing
            here changes your numbers until you act.
          </p>
          <ul className="mt-3 space-y-2">
            {unresolved.slice(0, 8).map((u) => (
              <li key={`${u.vendor}-${u.vendor_account_id}`} className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-white px-3 py-2 text-sm">
                <span className="flex-1 min-w-[220px]">
                  <span className="font-medium text-gray-900">{u.store_name || u.vendor_account_id}</span>
                  <span className="mt-0.5 block font-mono text-[11px] text-gray-500">
                    {u.vendor} · {u.vendor_account_id}
                    {u.state ? ` · ${u.state}` : ""}
                  </span>
                </span>
                <span className="rounded-md bg-amber-50 px-2 py-0.5 font-mono text-xs text-amber-800">{money(u.amount_usd)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="w-10 px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">In</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">Store</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">Dutchie</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">iHeartJane</th>
              </tr>
            </thead>
            <tbody>
              {account.stores.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">
                    No storefronts on this account yet — search below to add one.
                  </td>
                </tr>
              ) : (
                account.stores.map((s) => {
                  const badge = s.source ? SOURCE_BADGE[s.source] : null;
                  return (
                    <tr key={s.tenant_store_id} className="border-t border-gray-100 hover:bg-gray-50/70">
                      <td className="px-4 py-3 align-top">
                        <input
                          type="checkbox"
                          checked={s.included}
                          disabled={busyId === s.tenant_store_id}
                          onChange={() => toggleMember(s)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500/50"
                          aria-label={s.included ? `Exclude ${s.name ?? "store"}` : `Include ${s.name ?? "store"}`}
                        />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className={`font-medium ${s.included ? "text-gray-900" : "text-gray-400 line-through"}`}>
                          {s.name || s.license_number || s.tenant_store_id.slice(0, 8)}
                        </div>
                        <div className="mt-0.5 font-mono text-[11px] text-gray-500">
                          {s.license_number || "no licence"}
                          {s.city ? ` · ${s.city}` : ""}
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          {badge && (
                            <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${badge.cls}`}>
                              {badge.label}
                            </span>
                          )}
                          {s.confirmed_by_wtb && (
                            <span className="text-[10px] font-medium text-emerald-700" title="Confirmed by Where-to-Buy">✓ WTB</span>
                          )}
                          {s.confirmed_by_adspend && (
                            <span className="text-[10px] font-medium text-amber-600" title="Confirmed by ad spend">✓ ad-spend</span>
                          )}
                          {s.aliases.map((a) => (
                            <span
                              key={`${a.vendor}-${a.vendor_account_id}`}
                              className="font-mono text-[10px] text-gray-400"
                              title={`${a.vendor} calls this "${a.display_name ?? a.vendor_account_id}"${a.match_confidence ? ` (${a.match_confidence})` : ""}`}
                            >
                              {a.vendor}: {a.display_name || a.vendor_account_id}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right align-top"><CoverageCell cell={s.platforms.dutchie} /></td>
                      <td className="px-4 py-3 text-right align-top"><CoverageCell cell={s.platforms.iheartjane} /></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CrossSourceSearch slug={slug} onAddLicense={addLocation} />
    </div>
  );
}

/**
 * One keyword, every source the brand syncs. Results stay grouped by source
 * and every source is always listed: an empty group has to read as "searched,
 * nothing matched" rather than "we didn't look there".
 */
export function CrossSourceSearch({
  slug,
  onAddLicense,
}: {
  slug: string;
  onAddLicense?: (licenseNumber: string) => void;
}) {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [groups, setGroups] = useState<Array<{ source: string; rows: SearchHit[] }>>([]);
  const [searching, setSearching] = useState(false);
  const [ran, setRan] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const term = q.trim();
    if (term.length < 2) {
      setGroups([]);
      setRan(false);
      return;
    }
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await brandApi.get(
          `/api/brand/${encodeURIComponent(slug)}/accounts/search?q=${encodeURIComponent(term)}&scope=${scope}`
        );
        setGroups(r.results ?? []);
        setRan(true);
      } catch (err) {
        toast.push(`Search failed: ${(err as Error).message}`, "error");
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [q, scope, slug, toast]);

  const total = useMemo(() => groups.reduce((n, g) => n + g.rows.length, 0), [groups]);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Find a store to add</h2>
          <p className="mt-0.5 max-w-2xl text-xs text-gray-600">
            Searches every source synced for you.{" "}
            <span className="font-medium">My footprint</span> covers records your own data touches;{" "}
            <span className="font-medium">All California</span> widens to the public licence register and Google
            profiles so you can pull in a store we've never linked.
          </p>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-gray-300 bg-white">
          {(["mine", "all"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              aria-pressed={scope === s}
              className={`px-3 py-1.5 text-xs ${
                scope === s ? "bg-accent-600 font-medium text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {s === "mine" ? "My footprint" : "All California"}
            </button>
          ))}
        </div>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Store name, DBA, city, licence number, or a platform's own account name…"
        className="mt-3 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/50"
      />

      {searching && <div className="mt-3 text-xs text-gray-500">Searching…</div>}

      {ran && !searching && (
        <div className="mt-3 space-y-2">
          {total === 0 && (
            <div className="rounded-md border border-gray-200 bg-white px-3 py-3 text-xs text-gray-500">
              Nothing matched in any source.
            </div>
          )}
          {groups.map((g) => (
            <div key={g.source} className="overflow-hidden rounded-md border border-gray-200 bg-white">
              <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  {SOURCE_LABEL[g.source] ?? g.source}
                </span>
                <span className="font-mono text-[10px] text-gray-400">
                  {g.rows.length === 0 ? "no matches" : `${g.rows.length}`}
                  {PUBLIC_SOURCES.has(g.source) ? " · public" : " · yours"}
                </span>
              </div>
              {g.rows.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">Searched, nothing matched.</div>
              ) : (
                g.rows.map((row) => (
                  <div key={`${g.source}-${row.ref}`} className="flex flex-wrap items-center gap-3 border-b border-gray-50 px-3 py-2 last:border-b-0">
                    <div className="min-w-[220px] flex-1">
                      <div className="text-sm text-gray-900">{row.title || row.ref}</div>
                      <div className="mt-0.5 font-mono text-[11px] text-gray-500">{row.subtitle}</div>
                    </div>
                    {onAddLicense && row.license_number && (
                      <Button variant="secondary" onClick={() => onAddLicense(row.license_number!)}>
                        Add to account
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
